import { useCallback, useState, useMemo } from 'react';

import type {
  GridRowSelectionModel,
  GridRowId,
} from '@mui/x-data-grid-premium';

type UseGridSelectionOptions = {
  allIds?: GridRowId[];
};

export const useGridSelection = (options?: UseGridSelectionOptions) => {
  const [rowSelectionModel, setRowSelectionModel] =
    useState<GridRowSelectionModel>({
      type: 'include',
      ids: new Set<GridRowId>(),
    });

  const onRowSelectionModelChange = useCallback(
    (model: GridRowSelectionModel) => {
      setRowSelectionModel(model);
    },
    []
  );

  const onSetRowSelectionModelIds = useCallback((ids: GridRowId[]) => {
    setRowSelectionModel({
      type: 'include',
      ids: new Set(ids),
    });
  }, []);

  const onResetSelectionModel = useCallback(() => {
    setRowSelectionModel({ type: 'include', ids: new Set() });
  }, []);

  const ids = useMemo(() => {
    const model = rowSelectionModel;
    const selected = Array.from(model.ids);
    if (model.type === 'include') return selected;
    const all = options?.allIds ?? [];
    if (!all.length) return [];
    const excluded = model.ids;
    return all.filter((id) => !excluded.has(id));
  }, [rowSelectionModel, options?.allIds]);

  return {
    ids,
    rowSelectionModel,
    onRowSelectionModelChange,
    onSetRowSelectionModelIds,
    onResetSelectionModel,
    reset: onResetSelectionModel,
  };
};

export type GridSelectionType = ReturnType<typeof useGridSelection>;
export default useGridSelection;
